import { useState, useEffect } from 'react';
import { checkAllPermissions, PermissionCheckResult } from '../utils/permission-utils';

interface UsePermissionCheckResult {
  permissionResult: PermissionCheckResult | null;
  shouldShowDialog: boolean;
  dismissDialog: () => void;
  onPermissionsGranted: () => void;
}

/**
 * Hook to manage permission checking state and flow
 */
export const usePermissionCheck = (): UsePermissionCheckResult => {
  const [permissionResult, setPermissionResult] = useState<PermissionCheckResult | null>(null);
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [hasCheckedOnLoad, setHasCheckedOnLoad] = useState(false);

  const checkPermissions = async (): Promise<void> => {
    try {
      const result = await checkAllPermissions();
      setPermissionResult(result);

      if (!result.allPermissionsGranted) {
        setShouldShowDialog(true);
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
      setShouldShowDialog(true);
    }
  };

  const dismissDialog = () => {
    setShouldShowDialog(false);
  };

  const onPermissionsGranted = () => {
    setShouldShowDialog(false);
  };

  // Check permissions on initial load
  useEffect(() => {
    if (!hasCheckedOnLoad) {
      setHasCheckedOnLoad(true);
      checkPermissions();
    }
  }, [hasCheckedOnLoad]);

  // Reset dialog state when permissions are granted
  useEffect(() => {
    if (permissionResult?.allPermissionsGranted) {
      setShouldShowDialog(false);
    }
  }, [permissionResult]);

  return {
    permissionResult,
    shouldShowDialog,
    dismissDialog,
    onPermissionsGranted
  };
};